import { pluralize } from '../../../helpers/pluralize';
import { splitItineraryIntoLegs } from './splitItineraryLegs';

/** Narrowest segment shape these helpers read — kept structural so any generated segment fragment satisfies it. */
type RouteLabelSegment = {
  /** Segment departure; `iataCode` identifies the origin the trip must return to for a return trip. */
  departure?: { datetime?: string | null; iataCode?: string | null } | null;
  /** Segment arrival; `iataCode` detects the return to origin, the rest names the place. */
  arrival?: {
    datetime?: string | null;
    iataCode?: string | null;
    city?: string | null;
    name?: string | null;
  } | null;
  /** Timezone-correct flight time, read by the leg splitter. */
  durationInSeconds?: number | null;
};

type RouteLabelItinerary<TSegment extends RouteLabelSegment> = {
  segments?: readonly TSegment[] | null;
};

/** Shape of a journey: what the traveler is doing, independent of how any surface words it. */
export type FlightRouteKind = 'one-way' | 'return' | 'multi-stop';

export type FlightRoute<TSegment extends RouteLabelSegment> = {
  /** Journey shape, which decides the wording and whether a destination can be named at all. */
  kind: FlightRouteKind;
  /** Arrival endpoint that names the journey. Absent for multi-stop, which has no single destination. */
  destination?: TSegment['arrival'];
};

/**
 * Classify a set of flights by the shape of the journey. Reading the last arrival named the ORIGIN for a round trip
 * carried in one itinerary (a Sydney to Melbourne return arrives back in Sydney), so the destination is taken from the
 * end of the outbound leg instead. Returns the endpoint rather than a finished string because surfaces name places
 * differently: the app uses the city, the PDF uses the airport name.
 */
export function describeFlightRoute<TSegment extends RouteLabelSegment>(
  itineraries: readonly RouteLabelItinerary<TSegment>[],
): FlightRoute<TSegment> {
  const legs = itineraries
    .flatMap((itinerary) => splitItineraryIntoLegs(itinerary.segments ?? []))
    .filter((leg) => leg.length > 0);

  const outbound = legs[0];

  if (!outbound) {
    return { kind: 'one-way' };
  }

  const destination = outbound[outbound.length - 1]?.arrival;

  if (legs.length === 1) {
    return { kind: 'one-way', destination };
  }

  const finalLeg = legs[legs.length - 1];
  const origin = outbound[0]?.departure?.iataCode;
  const finalArrival = finalLeg[finalLeg.length - 1]?.arrival?.iataCode;

  // Only an out-and-back pair is a return trip; a longer loop through other cities is multi-stop even though it also
  // ends where it started.
  if (legs.length === 2 && origin && origin === finalArrival) {
    return { kind: 'return', destination };
  }

  return { kind: 'multi-stop' };
}

/**
 * Word a route for display. Kept in one place so every surface phrases the same journey identically, whichever way it
 * chose to name the destination.
 */
export function formatFlightRouteLabel(kind: FlightRouteKind, destinationName?: string | null): string {
  if (kind === 'multi-stop') {
    return 'Multi-stops';
  }

  const prefix = kind === 'return' ? 'Return flight' : 'Flight';
  return destinationName ? `${prefix} to ${destinationName}` : prefix;
}

/** Label a route the way the app names places, by city, falling back to the airport name. */
export function flightRouteLabel<TSegment extends RouteLabelSegment>(
  itineraries: readonly RouteLabelItinerary<TSegment>[],
): string {
  const { kind, destination } = describeFlightRoute(itineraries);
  return formatFlightRouteLabel(kind, destination?.city || destination?.name);
}

/**
 * Title one group of a price breakdown. Each group prices one offer, so naming the journey it covers tells the groups
 * of a multi-flight trip apart, where the same "Flights" heading repeated over every group does not. Checkout is the
 * exception: it prices the whole trip off the last leg's offer alone, so that group covers flights it never lists and
 * has to count them instead of naming the one it happens to carry.
 */
export function flightBreakdownLabel<TSegment extends RouteLabelSegment>(
  itineraries: readonly RouteLabelItinerary<TSegment>[],
  { offerCount, flightCount }: { offerCount: number; flightCount: number },
): string {
  if (flightCount > offerCount) {
    return pluralize('Flight', flightCount);
  }

  return flightRouteLabel(itineraries);
}
